// SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>
//
// SPDX-License-Identifier: MPL-2.0

import { get, writable } from 'svelte/store';
import { getApiUrl } from '../config';
import { get as keycloakget } from './CoreService';
import { snackbarStore } from './SnackbarStore';
import uiStore from './UIStore';

/**
 * @file
 *
 * Functions for storing and fetching configurations from the backends.
 *
 * The main idea is to make configurations from the backend available in frontend without the
 * need to define these configurations in frontend code in parallel.
 *
 * Main usage:
 *  getMicroserviceConfig(microservice, configId)
 *    -> if available: returns the configuration with configId for a microservice
 *    -> fetches the configuration from the backend for a microservice if not already done
 *    -> if errors appear: show the user a message and shows some more details in browser console
 */

/** Svelte store for configs received from the backend **/
const microserviceConfigStore = writable({});

/** For each microservice define the endpoint here **/
const MICROSERVICE_ENDPOINTS = {
  transcription: 'transcription/config',
};

/** Cache for in-flight fetch promises to avoid duplicate requests **/
const pendingFetches = {};

/**
 * Helper to show a simple message to the user.
 * @returns {string}
 */
function getDefaultUserError() {
  const uiTexts = get(uiStore.textStore)?.texts;
  return uiTexts?.backendConfigError;
}

/**
 * Fetches the API endpoint and stores the values in the store.
 * @param {string} microservice - Name of the microservice
 * @returns {Promise<void>}
 */
async function fetchAndCache(microservice) {
  // If a fetch is already in progress: return the same promise
  if (pendingFetches[microservice]) {
    return pendingFetches[microservice];
  }

  const endpoint = MICROSERVICE_ENDPOINTS[microservice];

  if (!endpoint) {
    throw new Error(`No endpoint for microservice "${microservice}" defined.`);
  }

  const url = `${getApiUrl()}/${endpoint}`;
  const fetchPromise = keycloakget(url)
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Error on requesting ${url}: HTTP ${response.status} - ${response.statusText}`);
      }
      const configs = await response.json();
      microserviceConfigStore.update((current) => ({
        ...current,
        [microservice]: { status: 'loaded', configs },
      }));
    })
    .finally(() => {
      // Promise aus dem Cache entfernen wenn fertig (egal ob Erfolg oder Fehler)
      delete pendingFetches[microservice];
    });

  pendingFetches[microservice] = fetchPromise;
  return fetchPromise;
}

/**
 * Returns the configuration value for a microservice.
 * @param {string} microservice - Name of the microservice
 * @param {string} configId    - ID of the requested configuration
 * @returns {Promise<any>}      - The configuration value, or void on error
 */
export async function getMicroserviceConfig(microservice, configId) {
  try {
    const currentStore = get(microserviceConfigStore);
    const serviceState = currentStore[microservice];

    // when the configs were not successfully loaded yet -> load it
    if (serviceState?.status !== 'loaded') {
      await fetchAndCache(microservice);
    }

    // re-read the store after potential fetch
    const updatedServiceState = get(microserviceConfigStore)[microservice];

    // check if the requested config actually exists
    if (Object.prototype.hasOwnProperty.call(updatedServiceState.configs, configId)) {
      return updatedServiceState.configs[configId];
    }

    // if we come here the requested config does not exist
    throw new Error(
      `The configuration "${configId}" for microservice "${microservice}" was not found in the backend response.`,
    );
  } catch (error) {
    snackbarStore.update((current) => ({
      ...current,
      snackbarOpen: true,
      snackbarSeverity: 'error',
      snackbarMessage: getDefaultUserError(),
    }));
    console.error(error.message);
  }
}
